import type {
  EmergencyStopChannelResult,
  EmergencyStopPositionResult,
  EmergencyStopPositionStatus,
  EmergencyStopResponse,
  MeResponse,
  OpenPosition,
  PositionListResponse,
  ReasonCode,
} from '../../api';

/** An open position FXMate opened: the only kind the stop may close (M4 R5, FXM-04 §7). */
export type FxmatePosition = Extract<OpenPosition, { origin: 'FXMATE' }>;

/**
 * What E10 can offer for "also close the positions" (M4 R3, R6, R7):
 * - `loading`: `GET /positions` has not answered; the option is disabled (proposal: closing
 *   without showing each result would break FXM-02 §6 case 6, the reason of R6);
 * - `unavailable`: the list failed; the option is disabled with `POSITIONS_UNAVAILABLE` (R6);
 * - `none`: no FXMate position; the option is hidden (R7);
 * - `available`: the FXMate positions the trader sees before choosing (R4).
 */
export type CloseOption =
  | { readonly kind: 'loading' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'none' }
  | { readonly kind: 'available'; readonly positions: readonly FxmatePosition[] };

/**
 * Derives {@link CloseOption} from the positions read. A list already received wins over a later
 * failure: the trader saw those results.
 */
export function closeOptionFrom(
  data: PositionListResponse | undefined,
  failed: boolean,
): CloseOption {
  if (data === undefined) return failed ? { kind: 'unavailable' } : { kind: 'loading' };
  const positions = data.positions.filter(
    (position): position is FxmatePosition => position.origin === 'FXMATE',
  );
  return positions.length === 0 ? { kind: 'none' } : { kind: 'available', positions };
}

/**
 * True when the position's current result can be shown (M4 §4 "No current result", provisional).
 * Hidden like on E8 while it opens (Q51) and once tracking is lost (Q54: no time to show it with),
 * and when the API sends none.
 */
export function hasCurrentResult(
  position: FxmatePosition,
): position is FxmatePosition & { floatingResult: NonNullable<FxmatePosition['floatingResult']> } {
  return (
    position.floatingResult !== null &&
    position.status !== 'OPENING' &&
    position.status !== 'TRACKING_LOST'
  );
}

/**
 * How many channels the stop will pause, from `GET /me` (M4 R2): those not already paused. A
 * channel paused before stays as it is (R13), so it is not announced again. `null` while unknown.
 */
export function channelsToPause(me: MeResponse | undefined): number | null {
  if (me === undefined) return null;
  return me.channels.filter((channel) => channel.copyState !== 'paused').length;
}

/** The pause state E10 shows for a channel: the stop's answer, overridden by a row retry. */
export type ChannelRow =
  | { readonly kind: 'paused' }
  | {
      readonly kind: 'failed';
      readonly reasonCode: ReasonCode | null;
      readonly retrying: boolean;
    };

/** True when every channel is paused, after retries: E10 then says "All copying is stopped". */
export function allChannelsPaused(rows: readonly ChannelRow[]): boolean {
  return rows.every((row) => row.kind === 'paused');
}

/** The state of a channel's row from a stop's answer (M4 R10). */
export function channelRowFrom(channel: EmergencyStopChannelResult): ChannelRow {
  return channel.result === 'PAUSED'
    ? { kind: 'paused' }
    : { kind: 'failed', reasonCode: channel.reasonCode, retrying: false };
}

/**
 * A position's row in E10's summary: a stop's answer, or `NO_LONGER_OPEN` once a later read of
 * `GET /positions` no longer lists a position whose close had failed or was running (M4 §4
 * "Closed meanwhile", provisional, Q104). `NO_LONGER_OPEN` is E10's own status, not the API's:
 * it carries no result and no reason, since the read does not say how the position ended.
 */
export type PositionRow =
  | EmergencyStopPositionResult
  | (Omit<EmergencyStopPositionResult, 'status' | 'result' | 'reasonCode'> & {
      readonly status: 'NO_LONGER_OPEN';
      readonly result: null;
      readonly reasonCode: null;
    });

/** Every status a position row can show (the API's, plus E10's `NO_LONGER_OPEN`). */
type PositionRowStatus = EmergencyStopPositionStatus | 'NO_LONGER_OPEN';

/**
 * What E10's summary shows, gathered over every stop of this visit: the first one, a channel's
 * retry and "Retry closing" are each a stop with its own answer (M4 R13).
 */
export interface StopSummary {
  /** Every channel any stop reported, in the order first reported. */
  readonly channels: readonly EmergencyStopChannelResult[];
  /** The row of each channel: the latest answer about it, or a retry in flight. */
  readonly rows: ReadonlyMap<string, ChannelRow>;
  /** Every position any stop reported, in the order first reported; the latest answer wins. */
  readonly positions: readonly PositionRow[];
  /**
   * The last stop that asked to close answered `PARTIAL` with nothing that explains it (see
   * {@link unexplainedClose}): E10 says the positions could not be closed, and never "All
   * copying is stopped" (M4 §4 "Positions not closed", provisional, Q107).
   */
  readonly positionsNotClosed: boolean;
}

/**
 * Adds a stop's answer to the summary (M4 §4 "Close failed", Q104). The new answer wins for each
 * channel and position it names; what it does not name keeps its earlier row, so a position
 * closed by the first stop keeps its realized result after "Retry closing", and a pause-only
 * retry (no positions) keeps every position row. `closePositions` is what that stop asked: only
 * a stop that asked to close can say the positions were not closed; a pause-only stop keeps the
 * earlier answer about it (Q107).
 */
export function mergeStop(
  previous: StopSummary | null,
  next: EmergencyStopResponse,
  closePositions: boolean,
): StopSummary {
  const channels = new Map((previous?.channels ?? []).map((c) => [c.subscriptionId, c]));
  const rows = new Map(previous?.rows ?? []);
  for (const channel of next.channels) {
    channels.set(channel.subscriptionId, channel);
    rows.set(channel.subscriptionId, channelRowFrom(channel));
  }
  const positions = new Map((previous?.positions ?? []).map((p) => [p.positionId, p]));
  for (const position of next.positions) positions.set(position.positionId, position);
  const positionsNotClosed = closePositions
    ? unexplainedClose(next)
    : (previous?.positionsNotClosed ?? false);
  return {
    channels: [...channels.values()],
    rows,
    positions: [...positions.values()],
    positionsNotClosed,
  };
}

/**
 * Statuses a fresh `GET /positions` can overrule: a close that failed, was still running, or was
 * waiting for its account. Settled rows (closed, already closed) are never touched. The same
 * statuses are those that explain a `PARTIAL` answer on their own (Q107).
 */
const UNSETTLED: ReadonlySet<PositionRowStatus> = new Set([
  'CLOSE_FAILED',
  'CLOSING',
  'PENDING_ACCOUNT',
]);

/**
 * True when a stop answered `PARTIAL` but no row says why: every channel paused, and no position
 * failed, still closing or waiting for its account (M4 §4 "Positions not closed", provisional,
 * Q107). The API answers so when it cannot read the open positions (202 with `positions: []`).
 * E10 reads `outcome` only for this (M4 R10), so the trader never believes positions were closed
 * when the stop could not even list them. Meaningful only for a stop that asked to close.
 */
export function unexplainedClose(answer: EmergencyStopResponse): boolean {
  if (answer.outcome !== 'PARTIAL') return false;
  if (answer.channels.some((channel) => channel.result === 'FAILED')) return false;
  return !answer.positions.some((position) => UNSETTLED.has(position.status));
}

/**
 * Marks as `NO_LONGER_OPEN` every `CLOSE_FAILED`, `CLOSING` or `PENDING_ACCOUNT` row whose position
 * is not in `openIds`, the ids of a `GET /positions` read after the stop (M4 §4 "Closed
 * meanwhile", provisional, Q104). Used when "Retry closing" reads the positions again: a position
 * closed meanwhile (by the market, the trader or the engine) loses its "Not closed" and its retry.
 * Returns `summary` itself when nothing changes, so a read that changes nothing renders nothing.
 * A row once marked is not restored if a later read lists the position again: the trader follows
 * it on E8, and only a later stop's answer naming the position replaces the row.
 */
export function markNoLongerOpen(summary: StopSummary, openIds: ReadonlySet<string>): StopSummary {
  const gone = (position: PositionRow): boolean =>
    UNSETTLED.has(position.status) && !openIds.has(position.positionId);
  if (!summary.positions.some(gone)) return summary;
  const positions = summary.positions.map((position): PositionRow =>
    gone(position)
      ? { ...position, status: 'NO_LONGER_OPEN', result: null, reasonCode: null }
      : position,
  );
  return { ...summary, positions };
}

/**
 * Positions still on their way to closed: followed on E8 through `GET /positions` (M4 R12).
 * `CLOSE_FAILED` is not one of them: nothing retries it today (Q40), E10 offers "Retry closing".
 */
export function hasClosesInProgress(positions: readonly PositionRow[]): boolean {
  return positions.some(
    (position) => position.status === 'CLOSING' || position.status === 'PENDING_ACCOUNT',
  );
}

/**
 * Positions whose close failed outright (C1 Decision 6): E10 offers a new stop that closes them.
 * A row marked `NO_LONGER_OPEN` no longer counts (Q104).
 */
export function hasFailedCloses(positions: readonly PositionRow[]): boolean {
  return positions.some((position) => position.status === 'CLOSE_FAILED');
}

/**
 * The reason text shown under a `CLOSING` position, if any (M4 §4 "Close in progress"):
 * `POSITION_CLOSE_FAILED` is never shown, since its C2 text says FXMate keeps trying, which is not
 * true while Q40 is open (Q104).
 */
export function closingReason(position: PositionRow): ReasonCode | null {
  if (position.status !== 'CLOSING' || position.reasonCode === null) return null;
  return position.reasonCode === 'POSITION_CLOSE_FAILED' ? null : position.reasonCode;
}
